/**
 * TenantDeactivationBanner — iter-121.40
 *
 * Sticky top-of-screen warning banner shown when the FE's /me poll detects
 * that the current user's tenant has been deactivated (by an SA or admin).
 * Replaces the previous hard-cutoff UX (user only finds out on their next
 * write attempt) with a graceful 120s soft-warning + auto sign-out.
 *
 * Lives just below ImpersonationBanner in AppShell so it never gets clipped
 * by content scroll. Renders nothing when the tenant is still active.
 */
import React, { useEffect, useState } from "react";
import { AlertOctagon, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";

export default function TenantDeactivationBanner() {
    const { user, tenantDeactivatedAt, tenantDeactivatedGraceMs, logout } = useAuth();
    // 1s tick so the countdown shrinks visibly without re-rendering the
    // whole AppShell. Only mounted while the banner is actually visible.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!tenantDeactivatedAt) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [tenantDeactivatedAt]);

    if (!user) return null;
    if (!tenantDeactivatedAt) return null;

    const elapsed = Math.max(0, now - tenantDeactivatedAt);
    const remaining = Math.max(0, Math.ceil((tenantDeactivatedGraceMs - elapsed) / 1000));

    return (
        <div
            className="bg-rose-500/10 border-b border-rose-500/30 text-rose-900 dark:text-rose-100 px-4 py-2.5 flex items-center justify-between gap-4 shrink-0"
            role="alert"
            data-testid="tenant-deactivated-banner">
            <div className="flex items-center gap-2.5 min-w-0">
                <AlertOctagon className="h-4 w-4 text-rose-600 dark:text-rose-300 shrink-0" />
                <div className="text-sm leading-snug">
                    <span className="font-semibold">Your entity has been deactivated.</span>
                    <span className="ml-1 opacity-90">
                        Please save your work — you'll be signed out automatically in&nbsp;
                        <span className="font-mono font-semibold" data-testid="tenant-deactivated-countdown">{remaining}s</span>.
                    </span>
                </div>
            </div>
            <Button
                size="sm"
                variant="outline"
                className="border-rose-500/40 hover:bg-rose-500/15 text-rose-700 dark:text-rose-200 rounded-md shrink-0"
                onClick={logout}
                data-testid="tenant-deactivated-signout-now">
                <LogOut className="h-3.5 w-3.5 mr-1.5" /> Sign out now
            </Button>
        </div>
    );
}
